// src/components/accueil/personnaliser/ReglageTelephone.tsx — SCE Issues
// « Mon accueil au téléphone » (lot 7 du chantier du 03/11, docs/chantier-accueil.md) :
// une colonne, les cartes du bureau dans l'ordre du modèle. Ici on les monte ou
// on les descend avec des flèches (pas de glisser au pouce, trop sujet aux
// erreurs en marchant), et on en retire du téléphone seul — la file choisie et
// les cartes imposées restent. Une carte « bureau seulement » du modèle (Mes
// déclarations, l'activité de l'opérateur) arrive éteinte : on peut l'allumer. Rien n'est enregistré avant « Terminé ».

import { useEffect, useState, type JSX } from 'react'
import { createPortal } from 'react-dom'
import { ArrowDown, ArrowUp, Lock, X } from 'lucide-react'
import {
  basculerTelephone,
  decalerTelephone,
  ordreTelephone,
  retirable,
  TELEPHONE_DEFAUT,
  visibleAuTelephone,
  type ModeleAccueil,
  type ReglageTelephone as Reglage,
} from '@/utils/dispositionAccueil'

export function ReglageTelephone({
  colonnes,
  modele,
  reglage,
  titreDe,
  onTerminer,
  onFermer,
}: {
  colonnes: string[][]
  modele: ModeleAccueil
  reglage: Reglage
  titreDe: (id: string) => string
  onTerminer: (r: Reglage) => void
  onFermer: () => void
}): JSX.Element {
  const [r, setR] = useState<Reglage>(reglage)
  const ordre = ordreTelephone(colonnes, modele, r)

  useEffect(() => {
    function surEchap(e: KeyboardEvent): void {
      if (e.key !== 'Escape') return
      e.stopImmediatePropagation()
      onFermer()
    }
    window.addEventListener('keydown', surEchap, true)
    return () => window.removeEventListener('keydown', surEchap, true)
  }, [onFermer])

  return createPortal(
    <div className="perso-telephone" role="dialog" aria-modal="true" aria-labelledby="perso-tel-titre">
      <div className="perso-telephone-entete">
        <button type="button" className="perso-menu" aria-label="Fermer sans enregistrer" onClick={onFermer}>
          <X size={20} aria-hidden="true" />
        </button>
        <h2 id="perso-tel-titre" style={{ margin: 0, fontSize: 18, fontWeight: 500, flex: 1 }}>
          Mon accueil au téléphone
        </h2>
      </div>
      <p style={{ margin: '0 16px 12px', fontSize: 13, lineHeight: '18px', color: 'var(--color-text-secondary)' }}>
        Les cartes de votre accueil, de haut en bas. Celles que vous retirez ici restent au bureau. Pour en ajouter ou en
        retirer partout : « Personnaliser » au bureau.
      </p>
      <ul className="perso-telephone-liste">
        {ordre.map((id, i) => {
          const fixe = !retirable(id, modele)
          const visible = visibleAuTelephone(id, modele, r)
          return (
            <li key={id} data-cache={!visible || undefined}>
              <span className="perso-telephone-titre">
                {titreDe(id)}
                {fixe && (
                  <span className="perso-cadenas">
                    <Lock size={12} aria-hidden="true" />
                    toujours là
                  </span>
                )}
              </span>
              <button type="button" className="perso-menu" aria-label={`Monter « ${titreDe(id)} »`} disabled={i === 0} onClick={() => setR({ ...r, ordre: decalerTelephone(ordre, id, -1) })}>
                <ArrowUp size={18} aria-hidden="true" />
              </button>
              <button type="button" className="perso-menu" aria-label={`Descendre « ${titreDe(id)} »`} disabled={i === ordre.length - 1} onClick={() => setR({ ...r, ordre: decalerTelephone(ordre, id, 1) })}>
                <ArrowDown size={18} aria-hidden="true" />
              </button>
              <button
                type="button"
                role="switch"
                aria-checked={visible}
                aria-label={`« ${titreDe(id)} » au téléphone`}
                className="perso-interrupteur"
                disabled={fixe}
                onClick={() => setR(basculerTelephone(r, id, modele))}
              >
                <span />
              </button>
            </li>
          )
        })}
      </ul>
      <div className="perso-telephone-pied">
        <button type="button" className="perso-lien" style={{ color: 'var(--color-black)' }} disabled={!r.ordre && !r.caches.length && !r.montrees.length} onClick={() => setR({ ...TELEPHONE_DEFAUT })}>
          Reprendre l’ordre du modèle
        </button>
        <button type="button" className="confirmation-confirmer" onClick={() => onTerminer(r)}>
          Terminé
        </button>
      </div>
    </div>,
    document.body,
  )
}
